import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useI18n } from '../../i18n'
import { getAccentStyles, getCVStyles, renderCVPage } from '../../render'
import type { CV } from '../../types/cv'
import './paginated-preview.css'

export interface PDFMargins {
  top: number
  right?: number
  bottom: number
  left?: number
  sides?: number
}

export interface PaginatedPreviewProps {
  cv: CV
  scale?: number
  margins?: PDFMargins
  showMarginGuides?: boolean
  showContentGuide?: boolean
  className?: string
}

// A4 dimensions in pixels at 96 DPI
const A4_HEIGHT_PX = 1123 // 297mm
const A4_WIDTH_PX = 794 // 210mm
const PAGE_GAP_PX = 24
const MIN_PREVIEW_SCALE = 0.32
const MAX_PREVIEW_SCALE = 0.82
const PREVIEW_HORIZONTAL_PADDING_PX = 48
const PAGE_BREAK_BUFFER_PX = 8
const MIN_PAGE_SLICE_HEIGHT_PX = 120
const DEFAULT_BREAK_PRIORITY = 1
const SIDEBAR_BREAK_PRIORITY = 2

// Convert mm to px at 96 DPI (1mm ≈ 3.78px)
const mmToPx = (mm: number) => Math.round(mm * 3.78)

// Default margins in mm
const DEFAULT_MARGINS: PDFMargins = { top: 15, right: 20, bottom: 15, left: 20 }

function clampPreviewScale(nextScale: number): number {
  return Math.min(MAX_PREVIEW_SCALE, Math.max(MIN_PREVIEW_SCALE, nextScale))
}

// Elements that should not be broken across pages
const NO_BREAK_RULES = [
  { selector: '.cv-sidebar .cv-header', priority: SIDEBAR_BREAK_PRIORITY },
  { selector: '.cv-sidebar .cv-section', priority: SIDEBAR_BREAK_PRIORITY },
  { selector: '.cv-section-skills', priority: SIDEBAR_BREAK_PRIORITY },
  { selector: '.cv-section-languages', priority: SIDEBAR_BREAK_PRIORITY },
  { selector: '.cv-section-certifications', priority: SIDEBAR_BREAK_PRIORITY },
  { selector: '.cv-header', priority: DEFAULT_BREAK_PRIORITY },
  { selector: '.cv-entry', priority: DEFAULT_BREAK_PRIORITY },
  { selector: '.cv-section-title', priority: DEFAULT_BREAK_PRIORITY },
  { selector: '.cv-skills-grid', priority: DEFAULT_BREAK_PRIORITY },
  { selector: '.cv-languages-list', priority: DEFAULT_BREAK_PRIORITY },
  { selector: '.cv-language-item', priority: DEFAULT_BREAK_PRIORITY },
  { selector: '.cv-custom-content p', priority: DEFAULT_BREAK_PRIORITY },
] as const

interface PageBreak {
  pageIndex: number
  startY: number
  endY: number
}

interface PageElementBounds {
  top: number
  bottom: number
  height: number
  priority: number
}

function getElementBounds(
  element: Element,
  containerRect: DOMRect,
  priority = DEFAULT_BREAK_PRIORITY
): PageElementBounds {
  const rect = element.getBoundingClientRect()
  const top = rect.top - containerRect.top
  const bottom = rect.bottom - containerRect.top
  return { top, bottom, height: bottom - top, priority }
}

function getBufferedPageEnd(elementTop: number, currentPageStart: number): number {
  const bufferedEnd = Math.max(currentPageStart, elementTop - PAGE_BREAK_BUFFER_PX)
  if (bufferedEnd - currentPageStart < MIN_PAGE_SLICE_HEIGHT_PX) return elementTop
  return bufferedEnd
}

function canKeepTogether(bounds: PageElementBounds, contentHeight: number): boolean {
  return bounds.height > 0 && bounds.height <= contentHeight
}

function splitsHigherPriorityElement(
  elements: PageElementBounds[],
  pageStart: number,
  pageEnd: number,
  priority: number
): boolean {
  return elements.some(
    (element) =>
      element.priority > priority &&
      element.top < pageEnd &&
      element.bottom > pageEnd &&
      element.bottom > pageStart
  )
}

function findPageBoundaryConflict(
  elements: PageElementBounds[],
  pageStart: number,
  pageEnd: number,
  contentHeight: number
): PageElementBounds | undefined {
  return elements.find((element) => {
    if (!canKeepTogether(element, contentHeight)) return false
    if (element.top <= pageStart + 1 || element.top >= pageEnd || element.bottom <= pageEnd) {
      return false
    }

    const nextPageEnd = getBufferedPageEnd(element.top, pageStart)
    if (nextPageEnd >= pageEnd || nextPageEnd <= pageStart + 1) return false

    return !splitsHigherPriorityElement(elements, pageStart, nextPageEnd, element.priority)
  })
}

/**
 * Paginated A4 preview component with intelligent page breaking
 * Avoids cutting elements mid-way like a real print engine
 */
export function PaginatedPreview({
  cv,
  scale = 0.5,
  margins = DEFAULT_MARGINS,
  showMarginGuides = false,
  showContentGuide = false,
  className = '',
}: PaginatedPreviewProps) {
  const { t } = useI18n()
  const scrollRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)

  // Calculate padding from margins
  const paddingTop = useMemo(() => mmToPx(margins.top), [margins.top])
  const paddingRight = useMemo(
    () => mmToPx(margins.right ?? margins.sides ?? DEFAULT_MARGINS.right ?? 20),
    [margins.right, margins.sides]
  )
  const paddingBottom = useMemo(() => mmToPx(margins.bottom), [margins.bottom])
  const paddingLeft = useMemo(
    () => mmToPx(margins.left ?? margins.sides ?? DEFAULT_MARGINS.left ?? 20),
    [margins.left, margins.sides]
  )
  const contentHeight = useMemo(
    () => A4_HEIGHT_PX - paddingTop - paddingBottom,
    [paddingTop, paddingBottom]
  )

  const [pageBreaks, setPageBreaks] = useState<PageBreak[]>([
    { pageIndex: 0, startY: 0, endY: contentHeight },
  ])
  const [contentHtml, setContentHtml] = useState('')
  const [previewContainerWidth, setPreviewContainerWidth] = useState<number | null>(null)
  const effectiveScale = useMemo(() => {
    if (!previewContainerWidth) return clampPreviewScale(scale)

    return clampPreviewScale((previewContainerWidth - PREVIEW_HORIZONTAL_PADDING_PX) / A4_WIDTH_PX)
  }, [previewContainerWidth, scale])

  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    const updateWidth = () => setPreviewContainerWidth(scrollElement.clientWidth)
    updateWidth()

    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(updateWidth)
    observer.observe(scrollElement)

    return () => observer.disconnect()
  }, [])

  // Render the CV using the same renderer the PDF export uses, so preview and
  // PDF never drift. Styles + accent override are inlined alongside the page.
  useEffect(() => {
    const styles = `${getCVStyles()}${getAccentStyles(cv.accentColor)}`
    setContentHtml(`<style>${styles}</style>${renderCVPage(cv)}`)
  }, [cv])

  // Calculate intelligent page breaks
  const calculatePageBreaks = useCallback(() => {
    if (!measureRef.current) return

    const container = measureRef.current
    const breaks: PageBreak[] = []
    let currentPageStart = 0
    let pageIndex = 0

    // Get all elements that shouldn't be broken
    const elements: PageElementBounds[] = []
    const containerRect = container.getBoundingClientRect()

    for (const { selector, priority } of NO_BREAK_RULES) {
      const nodes = container.querySelectorAll(selector)
      for (const node of nodes) {
        const bounds = getElementBounds(node, containerRect, priority)
        if (canKeepTogether(bounds, contentHeight)) {
          elements.push(bounds)
        }
      }
    }

    for (const section of container.querySelectorAll('.cv-section')) {
      const title = section.querySelector('.cv-section-title') as HTMLElement | null
      const firstContent = Array.from(section.children).find(
        (child) => child !== title && child instanceof HTMLElement
      ) as HTMLElement | undefined
      if (!title || !firstContent) continue

      const titleRect = title.getBoundingClientRect()
      const contentRect = firstContent.getBoundingClientRect()
      const sectionRect = section.getBoundingClientRect()
      const top = Math.min(sectionRect.top, titleRect.top) - containerRect.top
      const bottom = contentRect.bottom - containerRect.top
      const height = bottom - top
      const bounds = { top, bottom, height, priority: DEFAULT_BREAK_PRIORITY }

      if (canKeepTogether(bounds, contentHeight)) {
        elements.push(bounds)
      }
    }

    // Sort by top position
    elements.sort((a, b) => a.top - b.top || b.priority - a.priority || b.bottom - a.bottom)

    // Calculate where pages should break. Re-scan after every adjustment because
    // sidebar and main-column content can overlap vertically.
    const totalHeight = container.scrollHeight
    while (currentPageStart < totalHeight) {
      let currentPageEnd = Math.min(currentPageStart + contentHeight, totalHeight)
      let conflict = findPageBoundaryConflict(
        elements,
        currentPageStart,
        currentPageEnd,
        contentHeight
      )

      while (conflict) {
        const nextPageEnd = getBufferedPageEnd(conflict.top, currentPageStart)
        if (nextPageEnd >= currentPageEnd || nextPageEnd <= currentPageStart + 1) break

        currentPageEnd = nextPageEnd
        conflict = findPageBoundaryConflict(
          elements,
          currentPageStart,
          currentPageEnd,
          contentHeight
        )
      }

      breaks.push({
        pageIndex,
        startY: currentPageStart,
        endY: currentPageEnd,
      })
      pageIndex++
      currentPageStart = currentPageEnd
    }

    if (breaks.length === 0) {
      breaks.push({ pageIndex: 0, startY: 0, endY: contentHeight })
    }

    setPageBreaks(breaks)
  }, [contentHeight])

  // biome-ignore lint/correctness/useExhaustiveDependencies: HTML and margin changes must re-measure page breaks.
  useEffect(() => {
    calculatePageBreaks()
    if (document.fonts) {
      document.fonts.ready.then(calculatePageBreaks)
    }
    const timer = setTimeout(calculatePageBreaks, 150)
    return () => clearTimeout(timer)
  }, [contentHtml, calculatePageBreaks, margins])

  return (
    <div className={`paginated-preview ${className}`}>
      <div ref={scrollRef} className="paginated-preview-scroll">
        <div
          className="paginated-preview-pages"
          style={
            {
              '--preview-scale': effectiveScale,
              '--preview-page-width': `${A4_WIDTH_PX * effectiveScale}px`,
              '--preview-page-height': `${A4_HEIGHT_PX * effectiveScale}px`,
              '--page-gap': `${PAGE_GAP_PX}px`,
            } as React.CSSProperties
          }
        >
          {pageBreaks.map((page, idx) => (
            <div key={page.pageIndex} className="paginated-preview-page">
              <div className="paginated-preview-paper-frame">
                <div className="paginated-preview-paper">
                  {showMarginGuides && (
                    <div className="paginated-preview-margin-guides" aria-hidden="true">
                      <span
                        className="paginated-preview-margin-guide paginated-preview-margin-guide-top"
                        style={{ height: paddingTop }}
                      />
                      <span
                        className="paginated-preview-margin-guide paginated-preview-margin-guide-right"
                        style={{ top: paddingTop, bottom: paddingBottom, width: paddingRight }}
                      />
                      <span
                        className="paginated-preview-margin-guide paginated-preview-margin-guide-bottom"
                        style={{ height: paddingBottom }}
                      />
                      <span
                        className="paginated-preview-margin-guide paginated-preview-margin-guide-left"
                        style={{ top: paddingTop, bottom: paddingBottom, width: paddingLeft }}
                      />
                    </div>
                  )}
                  {showContentGuide && (
                    <div
                      className="paginated-preview-content-guide"
                      style={{
                        top: paddingTop,
                        right: paddingRight,
                        bottom: paddingBottom,
                        left: paddingLeft,
                      }}
                      aria-hidden="true"
                    />
                  )}
                  <div
                    className="paginated-preview-clip"
                    style={{
                      height: page.endY - page.startY,
                      overflow: 'hidden',
                      padding: `0 ${paddingRight}px 0 ${paddingLeft}px`,
                      marginTop: paddingTop,
                      marginBottom: paddingBottom + (contentHeight - (page.endY - page.startY)),
                    }}
                  >
                    <div
                      className="paginated-preview-content"
                      style={{
                        transform: `translateY(-${page.startY}px)`,
                      }}
                      // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML comes from the same escaped renderer used for PDF output.
                      dangerouslySetInnerHTML={{ __html: contentHtml }}
                    />
                  </div>
                </div>
              </div>
              <div className="paginated-preview-page-number">
                {t('builder.pageCount', { page: idx + 1, total: pageBreaks.length })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hidden measurement container */}
      <div
        ref={measureRef}
        className="paginated-preview-measure"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          width: A4_WIDTH_PX - paddingLeft - paddingRight,
        }}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML comes from the same escaped renderer used for PDF output.
        dangerouslySetInnerHTML={{ __html: contentHtml }}
      />
    </div>
  )
}
